import { useEffect, useState, useContext, useRef } from "react";
import { createBrowserRouter, useNavigate, useParams } from "react-router";

import { AppContext } from '../../context.js';
import { Chat as ChatIcon, ChevronDown } from "../../files/icons/index.js";
import { EditableDatePicker } from '../components/datePicker.js';
import { Status, Button } from "../components/component.js";
import Loading from "../components/loading.js";
import axios from "axios";

import { DatetimeToDate, DateToTimestamp, DateToFormat, calculateAge } from "../../utils/datetime.js";

import Chat from "../components/chat.js";

function Intake() {
  const context = useContext(AppContext);

  let { id } = useParams();

  let intakeID = id;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [chatTabSelected, setChatTabSelected] = useState("contact"); // contact, actions

  const [intake, setIntake] = useState(null);
  const [settings, setSettings] = useState(null);
  const [template, setTemplate] = useState(null);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({});

  // Intake status change confirmation
  const [changeIntakeStatusModalVisible, setChangeIntakeStatusModalVisible] = useState(false);
  const [changeIntakeStatusModalData, setChangeIntakeStatusModalData] = useState(null);

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/intakes', {
        filter: {
          id: [intakeID]
        }
      }),
      axios.get('/api/admin/settings', {
      }),
    ]).then(axios.spread((intakes, settings) => {
      setIntake(intakes.data.intakes[0]);
      setSettings(settings.data);

      for (let i = 0; i < settings.data.intakeTemplates.length; i++) {
        if (settings.data.intakeTemplates[i].id === intakes.data.intakes[0].templateID) {
          setTemplate(settings.data.intakeTemplates[i]);
          break;
        }
      }

      // Initialize form fields from intake data
      const intakeData = intakes.data.intakes[0];
      setFirstName(intakeData.firstName || "");
      setLastName(intakeData.lastName || "");
      setEmail(intakeData.email || "");
      setPhone(intakeData.phone || "");
      setMessage(intakeData.message || "");

      // Initialize form object from intake fields
      const initialForm = {};
      intakeData.fields.forEach((field) => {
        if (field.answers && field.answers.length > 0) {
          if (field.type === "date" && field.answers[0]) {
            initialForm[field.code] = new Date(field.answers[0]);
          } else {
            initialForm[field.code] = field.answers[0];
          }
        }
      });
      setForm(initialForm);

      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  function renderPages() {
    return intake.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function renderLine(pageID, line, isLastLine) {
    // Line example
    //[{"code": "firstName", "size": 6}, {"code": "lastName", "size": 6}]

    return (
      <div key={"page_"+pageID+"_line_"+line.id} className={"row " + (isLastLine ? "" : "mb-2")}>
        {line.map((lineField, index) => {
          let field = template.fields.find((f) => f.code === lineField.code);

          return (
            <div key={"page_"+pageID+"_line_"+line.id+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
              <div className="typo-strong-150">{field.title}:</div>

              {field.type === "text" && (
                <input type="text" placeholder="" value={form[field.code] || ""} onChange={(e) => changeField(field.code, e.target.value)} onBlur={() => updateIntake()} onKeyUp={(e) => keyUpField(e)} className="input sm typo-normal-150 w-100" />
              )}

              {field.type === "date" && (
                <EditableDatePicker 
                  date={form[field.code]}
                  onChange={(e) => { changeField(field.code, e); updateIntake(); }}
                  rightIcon={<ChevronDown width={16} height={16} color={'grey'} />}
                  defaultLevel="year"
                />
              )}

              {field.type === "radio" && (
                field.options.map((option) => {
                  return (
                    <div key={"radio_"+field.code+"_"+option} className={"item typo-normal-150 me-2 " + (form[field.code] == option ? "blue" : "grey")} onClick={(e) => { changeField(field.code, option); updateIntake(); }}>{option}</div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function changeField(name, value) {
    // Create a new object to avoid direct mutation
    const newForm = { ...form, [name]: value ? value : null };
    setForm(newForm);
  }

  function updateIntake() {
    let u = context.addSpiner();

    axios.post('/api/admin/intakes/update', {
      id: intake.id,
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone,
      message: message,
      form: form,
    }).then((response) => {
      // Update intake in state
      setIntake(prevIntake => ({
        ...prevIntake,
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone,
        message: message,
      }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function keyUpField(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function changeIntakeStatus(status) {
    if (status.reasons.length > 0) {
      setChangeIntakeStatusModalData(status);
      setChangeIntakeStatusModalVisible(true);
      return;
    }

    updateIntakeStatus(status.id, null);
  }

  function changeIntakeStatusConfirm(statusID, reasonID) {
    setChangeIntakeStatusModalVisible(false);
    setChangeIntakeStatusModalData(null);
    updateIntakeStatus(statusID, reasonID);
  }

  function updateIntakeStatus(statusID, reasonID) {
    let u = context.addSpiner();
    axios.post('/api/admin/intakes/updateStatus', {
      id: intake.id,
      statusID: statusID,
      reasonID: reasonID,
    }).then((response) => {
      setIntake({ ...intake, 
        statusType: response.data.statusType,
        statusID: response.data.statusID, 
        statusName: response.data.statusName, 
        statusReasonID: response.data.statusReasonID, 
        statusReasonName: response.data.statusReasonName,

        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderIntakeStatus() {
    const currentStatus = template.statuses.find((ps) => ps.id === intake.statusID);

    return (
      <Status color={currentStatus.color} muted={true} size={'small'} text={intake.statusName} subtext={intake.statusReasonName} />
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-xs-12">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {changeIntakeStatusModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setChangeIntakeStatusModalVisible(false)}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-6 col-xs-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Confirm intake status change</span><br/>
                    <span className="typo-normal-175">Choose the correct reason and click on it</span>
                  </div>
                  <div className="card-content">
                    {changeIntakeStatusModalData.reasons.map((reason, ri) => {
                      return (
                        <div key={"reason_" + ri} className="side-item" onClick={() => changeIntakeStatusConfirm(changeIntakeStatusModalData.id, reason.id)}>
                          <div>
                            <div className="typo-strong-175">{reason.title}</div>
                            <div className="typo-normal-150">{reason.description}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-start align-items-center">
                <span className="typo-hero-small">Intake Overview</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row">

          {/* Chat */}
          {/* <div className="col-xs-12">
            <div className="card shadowmore mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-start align-items-center">
                  <div className="d-flex justify-content-center align-items-center" style={{width: 20, height: 20}}>
                    <ChatIcon width={16} height={14} color={'grey'} />
                  </div>
                  <div className={"item typo-normal-150 ms-2 " + (chatTabSelected === "contact" ? "active" : "")}  onClick={() => setChatTabSelected("contact")} >Contact History</div>
                </div>
              </div>
              <div className="card-content sm">
                <Chat
                  intakeID={intake.id}
                />
              </div>
            </div>
          </div> */}

          {/* Intake details and actions */}
          <div className="col-xs-12 mb-4">

            <div className="card shadowmore mb-4">
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-6">
                    <div className="typo-strong-200">{template.title}</div>
                  </div>
                  <div className="col-xs-6 text-end">
                    {renderIntakeStatus()}
                    {!intake.closedAt ? (
                      <div className="typo-normal-150 mt-2">created at {DateToFormat(DatetimeToDate(intake.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(intake.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</div>
                    ) : (
                      <div className="typo-normal-150 mt-2">closed at {DateToFormat(DatetimeToDate(intake.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(intake.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header sm">
                <div className="typo-strong-175">
                  Required Fields
                </div>
              </div>
              <div className="card-content sm">
                <div className="row mb-2">
                  <div className="col-xs-4">
                    <div className="typo-strong-150">First Name:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={firstName} onChange={(e) => setFirstName(e.target.value)} onBlur={() => updateIntake()} onKeyUp={(e) => keyUpField(e)} />
                  </div>
                  <div className="col-xs-4">
                    <div className="typo-strong-150">Last Name:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={lastName} onChange={(e) => setLastName(e.target.value)} onBlur={() => updateIntake()} onKeyUp={(e) => keyUpField(e)} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-4">
                    <div className="typo-strong-150">Email Address:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => updateIntake()} onKeyUp={(e) => keyUpField(e)} />
                    <div className="typo-strong-150 mt-2">Phone Number:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => updateIntake()} onKeyUp={(e) => keyUpField(e)} />
                  </div>
                  <div className="col-xs-8">
                    <div className="typo-strong-150">Message:</div>
                    <textarea rows="4" placeholder="" className="input sm typo-normal-150 w-100" value={message} onChange={(e) => setMessage(e.target.value)} onBlur={() => updateIntake()} />
                  </div>
                </div>
              </div>
            </div>

            {renderPages()}

            {intake.importID && (
              <div className="card mt-4">
                <div className="card-header sm">
                  <div className="typo-strong-175">Import Group</div>
                  <div className="typo-normal-125 mb-2">This intake is part of an import group</div>
                  <div onClick={() => navigate(`/intakes?importID=${intake.importID}`)} className="item grey typo-normal-150">
                    View all intakes from this import
                  </div>
                </div>
              </div>
            )}

            {intake.patientID && (
              <div className="card mt-4">
                <div className="card-header sm">
                  <div className="typo-strong-175">Patient created</div>
                  <div className="typo-normal-125 mb-2">This form was used to create patient</div>
                  <Button onClick={() => navigate(`/patient/${intake.patientID}/card`)} color="blue" size="small" text="Open patient profile" />
                </div>
              </div>
            )}

            {intake.statusType === "TO_DO" && (
              <div className="card mt-4">
                <div className="card-header sm">
                  <div className="typo-strong-175">Create patient</div>
                  <div className="typo-normal-125 mb-2">This contact intake could be converted into patient or deleted</div>
                  <div onClick={() => navigate("/patient/create?intakeID=" + intake.id)} className="item blue typo-normal-150">
                    Create patient
                  </div>
                  <div onClick={() => changeIntakeStatus(template.statuses[2])} className="item grey typo-normal-150 ms-2">
                    Archive intake
                  </div>
                </div>
              </div>
            )}
          
            
          </div>
        
        </div>
      </div>
    </>
  );
}

export default Intake;
